"use client"

import { useState } from "react"
import { ArrowRight } from "lucide-react"

import { MarketingButton } from "@/components/marketing/marketing-action"
import { marketingFieldClass } from "@/components/marketing/marketing-field"
import { MarketingAlert } from "@/components/marketing/marketing-alert"
import { EmailLink } from "@/components/marketing/email-link"
import { storeNewsletterSignupEmail } from "@/lib/newsletter/continuation"
import { requestNewsletterSignup } from "@/lib/newsletter/signup-request"
import { AnalyticsEvent, track } from "@/lib/analytics"
import { EMAIL_RE } from "@/lib/utils"
import { HoneypotField } from "@/components/marketing/honeypot-field"

type Variant = "footer" | "inline" | "card"

interface Props {
  source?: string
  variant?: Variant
  heading?: string
  description?: string
  placeholder?: string
  submitLabel?: string
  confirmation?: string
}

const DEFAULTS = {
  heading: "One clean read before you overthink your reply.",
  description:
    "A short field note for the message, profile, or date you are actually dealing with.",
  placeholder: "Email address",
  submitLabel: "Get tomorrow's note",
  // The intro popup's own thank-you, word for word: the newsletter is one free
  // article a week now, so the old "Tomorrow morning" promise was not true.
  confirmation: "You're all set. Check your email. A free article lands in your inbox every week.",
}

// The HTML `pattern` attribute takes a string; the runtime check is the shared EMAIL_RE.
const EMAIL_PATTERN = "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"

/** What both newsletter forms (this one and the intro popup) say when
 * POST /api/newsletter answers `subscribed: false`: our email cannot reach the
 * address (a bounce or spam-complaint suppression, or a team address). An
 * address that only unsubscribed earlier is re-subscribed by the route and
 * never lands here. Never "check your email", because nothing will arrive. */
export function NewsletterUnreachableLine() {
  return (
    <>
      We can&apos;t send email to this address. Try a different one, or write to us at <EmailLink />.
    </>
  )
}

export function NewsletterSignup({
  source = "footer",
  variant = "footer",
  heading,
  description,
  placeholder,
  submitLabel,
  confirmation,
}: Props) {
  const [email, setEmail] = useState("")
  const [state, setState] = useState<"idle" | "loading" | "ok" | "error">("idle")
  const [error, setError] = useState("")
  const [unreachable, setUnreachable] = useState(false)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (state === "loading") return

    const normalized = email.trim().toLowerCase()
    if (!EMAIL_RE.test(normalized) || normalized.length > 254) {
      setError("Enter a valid email address.")
      setState("error")
      return
    }

    setState("loading")
    setError("")
    setUnreachable(false)

    // Bounded: a stalled connection gets the retryable network error, never an
    // endless "Sending…" (lib/newsletter/signup-request.ts).
    const result = await requestNewsletterSignup(normalized, source, e.currentTarget)
    if (result.kind === "error") {
      setError(result.message)
      setState("error")
      return
    }
    if (result.kind === "unreachable") {
      setError("unreachable")
      setUnreachable(true)
      setState("error")
      return
    }
    track(AnalyticsEvent.NewsletterSignup, { source })
    // Outside the request's try: the signup already succeeded, so a browser
    // that blocks site storage must not turn it into "Network error". Kept so
    // the create-account page prefills the address if he ever goes there.
    try {
      storeNewsletterSignupEmail(normalized)
    } catch {
      // The create page asks for the address again; nothing is lost.
    }
    // A plain thank-you in place, the way the intro popup does it.
    setState("ok")
  }

  const headingText = heading ?? DEFAULTS.heading
  const descriptionText = description ?? DEFAULTS.description
  const placeholderText = placeholder ?? DEFAULTS.placeholder
  const submitText = submitLabel ?? DEFAULTS.submitLabel
  const confirmationText = confirmation ?? DEFAULTS.confirmation

  const wrapperClass = {
    footer: "flex flex-col gap-3 md:gap-4",
    inline: "flex flex-col gap-5",
    card:
      "flex flex-col gap-5 border border-[var(--color-rule-strong)] bg-[var(--color-paper-high)] p-7 md:p-9",
  }[variant]

  const headingClass = {
    footer: "serious-display text-[1.28rem] leading-[1.15] text-[var(--color-ink)] md:text-[1.5rem]",
    inline: "serious-display text-[clamp(1.5rem,2.4vw,1.9rem)] leading-[1.15] text-[var(--color-ink)]",
    card: "serious-display text-[1.75rem] leading-[1.15] text-[var(--color-ink)]",
  }[variant]

  const descriptionClass = {
    footer: "text-[0.92rem] text-[var(--color-ink-muted)] leading-[1.55]",
    inline: "text-[1rem] text-[var(--color-ink-muted)] leading-[1.6] max-w-[36rem]",
    card: "text-[1rem] text-[var(--color-ink-muted)] leading-[1.6]",
  }[variant]

  const inputClass = marketingFieldClass({
    className:
      variant === "footer"
        ? "w-full px-3.5 py-2.5 pr-12 md:px-4"
        : "min-w-0 flex-1 px-4 py-3",
  })
  const buttonSize = variant === "footer" ? "md" : "lg"

  const emailInput = (
    <input
      type="email"
      name="email"
      required
      maxLength={254}
      pattern={EMAIL_PATTERN}
      title="Enter a valid email address."
      inputMode="email"
      aria-label="Email address"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${source}-newsletter-error` : undefined}
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder={placeholderText}
      autoComplete="email"
      className={inputClass}
    />
  )

  return (
    <div className={wrapperClass}>
      {(headingText || descriptionText) && (
        <div className="flex flex-col gap-2">
          {headingText && <p className={headingClass}>{headingText}</p>}
          {descriptionText && <p className={descriptionClass}>{descriptionText}</p>}
        </div>
      )}

      {state === "ok" ? (
        <MarketingAlert role="status" variant="success">
          {confirmationText}
        </MarketingAlert>
      ) : (
        <form method="post" onSubmit={handleSubmit} className="relative flex flex-col gap-2">
          <HoneypotField />
          {variant === "footer" ? (
            // Oura-style: one rounded field with the submit arrow INSIDE it (no
            // separate text button).
            <div className="relative">
              {emailInput}
              <button
                type="submit"
                disabled={state === "loading"}
                aria-label="Subscribe"
                className="absolute right-1 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-white transition-opacity hover:opacity-60 disabled:opacity-40"
              >
                <ArrowRight size={18} aria-hidden />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-stretch gap-2 sm:flex-row">
              {emailInput}
              <MarketingButton
                type="submit"
                disabled={state === "loading"}
                size={buttonSize}
              >
                {state === "loading" ? "Sending…" : submitText}
                <ArrowRight size={14} aria-hidden />
              </MarketingButton>
            </div>
          )}
          {error ? (
            <MarketingAlert id={`${source}-newsletter-error`} className="mt-2 py-2 px-3 text-[0.85rem]">
              {unreachable ? <NewsletterUnreachableLine /> : error}
            </MarketingAlert>
          ) : null}
        </form>
      )}
    </div>
  )
}
